import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import Spinner from './Spinner';

const AdminAuthWrapper = ({ children, darkMode }) => {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Authorized admin emails
  const AUTHORIZED_ADMIN_EMAILS = ['doctorworldconsultancy@gmail.com', 'officialvishwateja@gmail.com'];

  useEffect(() => {
    console.log('AdminAuthWrapper: Setting up auth listener...');
    
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      console.log('AdminAuthWrapper: Auth state changed:', firebaseUser);
      
      if (firebaseUser) {
        console.log('AdminAuthWrapper: User email:', firebaseUser.email);
        console.log('AdminAuthWrapper: Authorized emails:', AUTHORIZED_ADMIN_EMAILS.join(', '));
        
        // Check if the user's email matches the authorized admin email
        if (AUTHORIZED_ADMIN_EMAILS.includes(firebaseUser.email)) {
          console.log('AdminAuthWrapper: Admin authorized - email matches');
          setIsAdminAuthenticated(true);
          
          // Store admin session info
          localStorage.setItem('adminEmail', firebaseUser.email);
          localStorage.setItem('adminName', firebaseUser.displayName || 'Admin');
          localStorage.setItem('adminPhoto', firebaseUser.photoURL || '');
          localStorage.setItem('isAdminAuthenticated', 'true');
        } else {
          console.log('AdminAuthWrapper: Access denied - unauthorized email');
          setIsAdminAuthenticated(false);
          localStorage.removeItem('adminEmail');
          localStorage.removeItem('adminName');
          localStorage.removeItem('adminPhoto');
          localStorage.removeItem('isAdminAuthenticated');
          
          // Redirect unauthorized users silently
          navigate('/admin-login');
        }
      } else {
        console.log('AdminAuthWrapper: No user authenticated');
        setIsAdminAuthenticated(false);
        localStorage.removeItem('adminEmail');
        localStorage.removeItem('adminName');
        localStorage.removeItem('adminPhoto');
        localStorage.removeItem('isAdminAuthenticated');
        navigate('/admin-login');
      }
      
      setLoading(false);
    });

    // Check for existing admin session on mount
    const checkExistingSession = () => {
      const isAuthenticated = localStorage.getItem('isAdminAuthenticated');
      const adminEmail = localStorage.getItem('adminEmail');
      
      if (isAuthenticated === 'true' && AUTHORIZED_ADMIN_EMAILS.includes(adminEmail)) {
        console.log('AdminAuthWrapper: Found valid admin session');
        // Auth state change will handle the rest
      } else {
        console.log('AdminAuthWrapper: No valid admin session found');
        localStorage.removeItem('adminEmail');
        localStorage.removeItem('adminName');
        localStorage.removeItem('adminPhoto');
        localStorage.removeItem('isAdminAuthenticated');
      }
    };

    checkExistingSession();

    return () => {
      console.log('AdminAuthWrapper: Cleaning up auth listener');
      unsubscribe();
    };
  }, [navigate]);

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        darkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="text-center">
          <Spinner size="lg" />
          <p className={`mt-4 text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Verifying admin access...
          </p>
        </div>
      </div>
    );
  }

  // Show unauthorized message if not admin
  if (!isAdminAuthenticated) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        darkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="text-center max-w-md mx-auto p-6">
          <div className="mb-6">
            <svg className="w-16 h-16 mx-auto text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L5.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Access Denied
          </h2>
          <p className={`text-lg mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            You are not authorized to access the admin area.
          </p>
          <p className={`text-sm mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Please contact support if you believe this is an error.
          </p>
          <button
            onClick={() => navigate('/admin-login')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-all duration-300"
          >
            Go to Admin Login
          </button>
        </div>
      </div>
    );
  }

  // Render admin content if authenticated
  return children;
};

export default AdminAuthWrapper;
